import React from 'react';
import {
  ArrowRightIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  RectangleGroupIcon,
  ChatBubbleLeftRightIcon,
  BoltIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { PBI_PAGE } from '../../../../content/enterpriseMessaging';
import { PBI_SECTION_MEDIA, mediaUrl, PRODUCT_MEDIA } from '../../../../content/productMedia';
import TalkToDataIntro from './TalkToDataIntro';
import CoWorkShowcase from './CoWorkShowcase';
import DiscoverySection from './DiscoverySection';
import ContextGapFoundation from './ContextGapFoundation';
import AnalyticsGovernanceSection from './AnalyticsGovernanceSection';
import ConversationalAgentsSection from './ConversationalAgentsSection';
import CommandHubSection from './CommandHubSection';
import ProductMediaFigure from '../../shared/ProductMediaFigure';
import SectionSurface from '../../shared/SectionSurface';
import HeroPullQuote from '../../shared/HeroPullQuote';
import ReadyToBuild from './GetStarted';
import DeliveryBand from '../../shared/DeliveryBand';

/* Accent tokens for cards/panels; section backgrounds use SectionSurface + page canvas. */
const ACCENT = {
  neutral: {
    check: 'text-brand-500',
    rule: 'border-l-brand-500',
    chip: 'bg-brand-50 text-brand-700 ring-brand-100',
  },
  subtle: {
    check: 'text-brand-500',
    rule: 'border-l-brand-500',
    chip: 'bg-brand-50 text-brand-700 ring-brand-100',
  },
  semantic: {
    check: 'text-brand-900',
    rule: 'border-l-brand-900',
    chip: 'bg-[rgba(0,18,63,0.08)] text-brand-900 ring-[rgba(0,18,63,0.16)]',
  },
  operational: {
    check: 'text-accent-700',
    rule: 'border-l-accent-400',
    chip: 'bg-accent-50 text-accent-700 ring-accent-100',
  },
};

/** Section bands chained on PageFlow canvas (hero → … → ready to build). */
const PBI_SECTION = {
  neutral: { variant: 'subtle', decor: 'none', connectTo: 'neutral' },
  subtle: { variant: 'subtle', decor: 'none', connectTo: 'neutral' },
  semantic: { variant: 'accent-semantic', decor: 'rings', connectTo: 'purple' },
  operational: { variant: 'dots', decor: 'contour', connectTo: 'green' },
};

function PBISection({ accent = 'neutral', id, grid = false, className = '', tight = true, children }) {
  const cfg = PBI_SECTION[accent] || PBI_SECTION.neutral;
  const decor = grid ? 'mesh' : cfg.decor;
  return (
    <SectionSurface
      as="section"
      id={id}
      variant={cfg.variant}
      decor={decor}
      connectTo={cfg.connectTo}
      bleed="both"
      flowTop
      flowBottom
      overlap
      className={`scroll-mt-24 ${className}`.trim()}
    >
      <div className={`container-x ${tight ? 'section-tight' : 'section'}`}>{children}</div>
    </SectionSurface>
  );
}

function BulletList({ items, accent = 'neutral' }) {
  const a = ACCENT[accent] || ACCENT.neutral;
  return (
    <ul className="mt-4 space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-base leading-relaxed text-ink-600">
          <CheckIcon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${a.check}`} aria-hidden="true" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/* ---------------- Inline section visuals (abstract data-flow mocks) -------- */

function PanelFrame({ accent = 'neutral', children, label }) {
  const a = ACCENT[accent] || ACCENT.neutral;
  return (
    <div className="relative w-full min-w-0 max-w-lg mx-auto lg:mx-0">
      <div className="rounded-2xl border border-ink-200/80 bg-white/90 p-5 shadow-md backdrop-blur-sm">
        <div className="mb-4 flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
          {label && (
            <span
              className={`ml-2 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${a.chip}`}
            >
              {label}
            </span>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

function SprawlVisual({ accent }) {
  const cards = [
    'rotate-[-4deg]',
    'rotate-[3deg] translate-y-2',
    'rotate-[2deg]',
    'rotate-[-2deg] translate-y-1',
    'rotate-[5deg]',
    'rotate-[-3deg]',
  ];
  return (
    <PanelFrame accent={accent} label="Sprawl">
      <div className="grid grid-cols-3 gap-3">
        {cards.map((tilt, i) => (
          <div
            key={i}
            className={`flex h-16 flex-col justify-between rounded-lg border border-ink-200 bg-surface-subtle p-2 shadow-xs ${tilt}`}
          >
            <div className="h-1.5 w-8 rounded-full bg-ink-200" />
            <div className="flex items-end gap-1">
              <div className="h-4 w-1.5 rounded-sm bg-ink-300" />
              <div className="h-6 w-1.5 rounded-sm bg-ink-300" />
              <div className="h-3 w-1.5 rounded-sm bg-ink-300" />
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-center text-[11px] font-medium text-ink-400">
        Duplicated · disconnected · hard to find
      </p>
    </PanelFrame>
  );
}

const GALLERY_THUMBS = [
  { src: mediaUrl(PRODUCT_MEDIA.semantichr_split_screen1), label: 'Workforce', alt: 'Headcount summary embed' },
  { src: mediaUrl(PRODUCT_MEDIA.semantichr_split_screen2), label: 'Attrition', alt: 'Terminations dashboard' },
  { src: mediaUrl(PRODUCT_MEDIA.semantichr_chat_only1), label: 'Headcount Q&A', alt: 'Chat analytics' },
  { src: mediaUrl(PRODUCT_MEDIA.semantichr_chat_only2), label: 'Pressure', alt: 'Termination pressure' },
];

function GalleryVisual({ accent }) {
  return (
    <PanelFrame accent={accent} label="BI gallery">
      <div className="flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-3 py-2 shadow-xs">
        <MagnifyingGlassIcon className="h-4 w-4 text-brand-900" />
        <span className="text-xs text-ink-500">Search dashboards, metrics, semantic models…</span>
      </div>
      <div className="mt-3 -mx-1 flex gap-2.5 overflow-x-auto pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:pb-0">
        {GALLERY_THUMBS.map((t) => (
          <div
            key={t.label}
            className="group relative min-w-[7.5rem] shrink-0 overflow-hidden rounded-lg border border-brand-100 shadow-xs ring-1 ring-inset ring-white/40 sm:min-w-0"
          >
            <img
              src={t.src}
              alt={t.alt || `${t.label} dashboard`}
              loading="lazy"
              className="h-auto max-h-28 w-full object-contain object-top bg-surface-subtle/40"
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-t from-brand-900/55 via-brand-900/10 to-transparent"
            />
            <span className="absolute bottom-1 left-1.5 text-[9px] font-semibold uppercase tracking-wide text-white/90">
              {t.label}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-4 text-center text-[11px] font-medium text-ink-400">
        Cataloged · governed · certified
      </p>
    </PanelFrame>
  );
}

function EmbeddedVisual({ accent }) {
  return (
    <PanelFrame accent={accent} label="Embedded">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2 rounded-lg border border-brand-100 bg-gradient-to-br from-brand-50/60 to-white p-3 shadow-xs">
          <div className="mb-2 h-1.5 w-16 rounded-full bg-brand-200" />
          <div className="flex h-20 items-end gap-1.5">
            {[8, 14, 10, 18, 12, 22, 16].map((h, i) => (
              <div
                key={i}
                style={{ height: `${h * 3}px` }}
                className="w-2 rounded-sm bg-gradient-to-t from-brand-400 to-accent-400"
              />
            ))}
          </div>
        </div>
        <div className="space-y-2">
          {['Page', 'Visual', 'Filter', 'Metric'].map((t) => (
            <div
              key={t}
              className="flex items-center gap-1.5 rounded-md border border-brand-100 bg-brand-50/70 px-2 py-1.5"
            >
              <RectangleGroupIcon className="h-3.5 w-3.5 text-brand-900" />
              <span className="text-[10px] font-semibold text-brand-900">{t}</span>
            </div>
          ))}
        </div>
      </div>
      <p className="mt-4 text-center text-[11px] font-medium text-ink-400">
        “Where is the dashboard?” → “What insight do I need?”
      </p>
    </PanelFrame>
  );
}

function IntegrationVisual({ accent }) {
  const nodes = [
    { label: 'Power BI', icon: Squares2X2Icon, tone: 'brand' },
    { label: 'Snowflake', icon: CircleStackIcon, tone: 'green' },
    { label: 'Streamlit', icon: BoltIcon, tone: 'green' },
  ];
  return (
    <PanelFrame accent={accent} label="Integration">
      <div className="flex items-center justify-between gap-2">
        {nodes.map((n, i) => (
          <React.Fragment key={n.label}>
            <div className="flex flex-1 flex-col items-center gap-1.5">
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-xl border shadow-xs ${
                  n.tone === 'green'
                    ? 'border-theme-green-100 bg-theme-green-50'
                    : 'border-brand-100 bg-brand-50'
                }`}
              >
                <n.icon
                  className={`h-6 w-6 ${n.tone === 'green' ? 'text-theme-green-600' : 'text-brand-600'}`}
                />
              </div>
              <span className="text-[10px] font-semibold text-ink-600">{n.label}</span>
            </div>
            {i < nodes.length - 1 && (
              <ArrowRightIcon className="h-4 w-4 flex-shrink-0 text-ink-300" />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {['Dashboards', 'Data tables', 'Actions'].map((t) => (
          <div
            key={t}
            className="rounded-md border border-theme-green-100 bg-gradient-to-br from-white to-theme-green-50/60 px-2 py-1.5 text-center text-[10px] font-semibold text-theme-green-700"
          >
            {t}
          </div>
        ))}
      </div>
      <p className="mt-4 text-center text-[11px] font-medium text-ink-400">
        One governed, interactive analytics hub
      </p>
    </PanelFrame>
  );
}

function ConversationalVisual({ accent }) {
  return (
    <PanelFrame accent={accent} label="Cortex Analyst">
      <div className="space-y-2.5">
        <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-brand-700 px-3 py-2 text-[11px] text-white shadow-xs">
          Which dashboard shows attrition by department this quarter?
        </div>
        <div className="flex items-start gap-2">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-100">
            <ChatBubbleLeftRightIcon className="h-4 w-4 text-brand-900" />
          </div>
          <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-brand-100 bg-white px-3 py-2 text-[11px] text-ink-700 shadow-xs">
            Found <span className="font-semibold text-brand-900">Workforce Attrition</span>, certified
            metric, owned by People Analytics.
            <div className="mt-2 flex items-end gap-1">
              {[10, 16, 9, 14, 20].map((h, i) => (
                <div
                  key={i}
                  style={{ height: `${h}px` }}
                  className="w-2 rounded-sm bg-gradient-to-t from-brand-400 to-accent-400"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <p className="mt-4 text-center text-[11px] font-medium text-ink-400">
        Governed answers grounded in Snowflake semantics
      </p>
    </PanelFrame>
  );
}

function RbacVisual({ accent, scopeExamples = [] }) {
  const stages = [
    { label: 'HRIS / source', sub: 'HRIS · CRM · ERP' },
    { label: 'Snowflake CORE', sub: 'DDM + row access policies' },
    { label: 'REPORTING layer', sub: 'Governed · secure views' },
    { label: 'Analytics surfaces', sub: 'PBI · Streamlit · Cortex' },
  ];
  return (
    <PanelFrame accent={accent} label="Enforcement pipeline">
      <div className="space-y-2">
        {stages.map((stage, i) => (
          <React.Fragment key={stage.label}>
            <div className="rounded-lg border border-brand-100 bg-gradient-to-r from-white to-brand-50/50 px-3 py-2.5">
              <p className="text-[11px] font-semibold text-ink-800">{stage.label}</p>
              <p className="text-[10px] text-ink-500">{stage.sub}</p>
            </div>
            {i < stages.length - 1 && (
              <div className="flex justify-center py-0.5" aria-hidden="true">
                <ArrowRightIcon className="h-3.5 w-3.5 rotate-90 text-accent-400" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      {scopeExamples.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {scopeExamples.map((ex) => (
            <span
              key={ex.role}
              className="rounded-md border border-ink-200 bg-white px-2 py-1 text-[9px] font-medium text-ink-600"
              title={ex.scope}
            >
              {ex.role}
            </span>
          ))}
        </div>
      )}
      <p className="mt-4 text-center text-[11px] font-medium text-ink-400">
        Same scope at warehouse, BI embed, and copilot
      </p>
    </PanelFrame>
  );
}

function OperationalVisual({ accent }) {
  const rows = [
    { label: 'Headcount variance', state: 'ok' },
    { label: 'Overtime threshold', state: 'alert' },
    { label: 'Pipeline coverage', state: 'warn' },
  ];
  const dot = { ok: 'bg-theme-green-500', warn: 'bg-amber-400', alert: 'bg-rose-500' };
  return (
    <PanelFrame accent={accent} label="Operational">
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-center justify-between rounded-lg border border-ink-200 bg-white px-3 py-2 shadow-xs"
          >
            <div className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${dot[r.state]}`} />
              <span className="text-[11px] font-medium text-ink-700">{r.label}</span>
            </div>
            <div className="flex items-end gap-0.5">
              {[6, 10, 7, 12, 9, 14].map((h, i) => (
                <div
                  key={i}
                  style={{ height: `${h}px` }}
                  className="w-1 rounded-sm bg-theme-green-300"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-center text-[11px] font-medium text-ink-400">
        Alerts · flags · triggers · decision workflows
      </p>
    </PanelFrame>
  );
}

const VISUALS = {
  sprawl: SprawlVisual,
  gallery: GalleryVisual,
  embedded: EmbeddedVisual,
  integration: IntegrationVisual,
  conversational: ConversationalVisual,
  operational: OperationalVisual,
  rbac: RbacVisual,
};

function SectionVisual({ name, accent, scopeExamples }) {
  const Cmp = VISUALS[name];
  if (!Cmp) return null;
  if (name === 'rbac') {
    return <RbacVisual accent={accent} scopeExamples={scopeExamples} />;
  }
  return <Cmp accent={accent} />;
}

/* --------------------------------- Sections -------------------------------- */

function NarrativeSection({
  section,
  imageRight = true,
  visualOnly = false,
  mediaMap = PBI_SECTION_MEDIA,
}) {
  const accent = section.accent || 'neutral';
  const mediaId = visualOnly ? null : mediaMap[section.id];
  return (
    <PBISection accent={accent} id={section.id} grid={section.visual === 'gallery'} tight>
      <div
        className={`grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center ${
          !imageRight ? 'lg:[&>*:first-child]:order-2' : ''
        }`}
      >
        <div>
          <p className="eyebrow">{section.eyebrow}</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            {section.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-ink-600">{section.body}</p>
          {section.bullets?.length > 0 && <BulletList items={section.bullets} accent={accent} />}
        </div>
        <div className="flex justify-center lg:justify-end min-w-0 w-full">
          {mediaId ? (
            <ProductMediaFigure
              id={mediaId}
              aspect="natural"
              className="w-full max-w-xl"
            />
          ) : (
            <SectionVisual
              name={section.visual}
              accent={accent}
              scopeExamples={section.scopeExamples}
            />
          )}
        </div>
      </div>
    </PBISection>
  );
}

const PILL_CLASSES = ['badge-migration', 'badge-semantic', 'badge-brand', 'badge-enterprise', 'badge-migration', 'badge-semantic'];

function TopEdgePill({ text, badgeClass = 'badge-enterprise' }) {
  return (
    <span
      className={`absolute -top-3 left-1/2 z-10 max-w-[calc(100%-2rem)] -translate-x-1/2 ${badgeClass} whitespace-normal break-words shadow-sm`}
    >
      {text}
    </span>
  );
}

export function PBIHero() {
  const hero = PBI_PAGE.hero;
  return (
    <SectionSurface
      variant="wash"
      decor="none"
      connectTo="brand"
      underNav
      bleed="bottom"
      flowBottom
      overlap
    >
      <div className="container-x pt-12 pb-2 sm:pt-16 sm:pb-4 lg:pt-20">
        <div className="relative mx-auto max-w-3xl mt-4 text-center">
          <TopEdgePill text={hero.eyebrow} badgeClass="badge-enterprise" />
          <div className="relative px-6 py-8 sm:px-10 sm:py-10">
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-ink-900">
              {hero.title}
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-ink-600 leading-relaxed">{hero.body}</p>

            <HeroPullQuote eyebrow="Conversational analytics">{hero.quote}</HeroPullQuote>

            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {hero.platforms.map((platform, index) => (
                <span key={platform} className={PILL_CLASSES[index % PILL_CLASSES.length]}>
                  {platform}
                </span>
              ))}
            </div>

            <div className="mt-8 flex w-full min-w-0 flex-col sm:flex-row items-center justify-center gap-3">
              <a href={hero.primaryCta.href} className="btn-primary btn-lg">
                {hero.primaryCta.label}
                <ArrowRightIcon className="h-4 w-4" />
              </a>
              <a href={hero.secondaryCta.href} className="btn-secondary btn-lg">
                {hero.secondaryCta.label}
              </a>
            </div>
          </div>
        </div>
        <TalkToDataIntro />
      </div>
    </SectionSurface>
  );
}

const OUTCOME_ICONS = [
  Squares2X2Icon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  BoltIcon,
  LinkIcon,
];

/* Blue primary, with purple + green as rotating secondary accents. */
const OUTCOME_TONES = [
  { chip: 'bg-brand-50 text-brand-600 ring-brand-100', bar: 'from-brand-400 to-brand-500' },
  { chip: 'bg-brand-50 text-brand-900 ring-brand-100', bar: 'from-brand-400 to-accent-400' },
  { chip: 'bg-theme-green-50 text-theme-green-600 ring-theme-green-100', bar: 'from-brand-400 to-theme-green-500' },
];

function OutcomesSection() {
  const { outcomes } = PBI_PAGE;
  return (
    <PBISection accent="neutral" tight>
      <div className="mx-auto max-w-3xl text-center">
        <p className="eyebrow">{outcomes.eyebrow}</p>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
          {outcomes.title}
        </h2>
      </div>
      <div className="mx-auto mt-8 grid max-w-6xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {outcomes.items.map((item, i) => {
          const Icon = OUTCOME_ICONS[i] || Squares2X2Icon;
          const tone = OUTCOME_TONES[i % 3];
          return (
            <div
              key={item.title}
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-ink-200/80 bg-white p-6 shadow-xs transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <span
                aria-hidden="true"
                className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${tone.bar}`}
              />
              <div className="flex items-center gap-3">
                <span
                  className={`flex h-11 w-11 items-center justify-center rounded-xl ring-1 ring-inset ${tone.chip}`}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="text-base font-semibold text-ink-900">{item.title}</h3>
              </div>
              <p className="mt-4 text-base leading-relaxed text-ink-600">{item.body}</p>
            </div>
          );
        })}
      </div>
    </PBISection>
  );
}

export function PBISections() {
  return (
    <>
      <CoWorkShowcase />
      <DiscoverySection />
      <ContextGapFoundation />
      <AnalyticsGovernanceSection />
      <ConversationalAgentsSection />
      <CommandHubSection />
      <OutcomesSection />
      <DeliveryBand id="delivery" compact variant="muted" decor="dots" />
      <ReadyToBuild />
    </>
  );
}

export { PBISection, NarrativeSection };
